// src/pages/StudentProgress.jsx — analytics dashboard for the student (/progress).
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { motion } from "framer-motion";
import {
  HiTrendingUp, HiFilter, HiChevronLeft, HiChevronRight,
  HiSearch, HiFire, HiStar, HiClock, HiPlay, HiArrowSmUp, HiArrowSmDown,
} from "react-icons/hi";
import { db } from "../components/firebase/firebase";
import { useAuth } from "../context/AuthContext";
import Loader from "../components/common/Loader";
import { SEO } from "../components/common/SEO";
import SubjectBars from "../components/student/SubjectBars";
import StreakCalendar from "../components/student/StreakCalendar";
import AnimatedCounter from "../components/student/AnimatedCounter";
import TrendChart from "../components/student/TrendChart";
import { WEAK_THRESHOLD } from "../lib/gamification";
import { weekKey, goalProgress, currentWeeklyXp } from "../lib/goals";

const BOARD_LABELS = { state: "State Board", cbse: "CBSE", competitive: "Competitive" };
const PAGE_SIZE = 10;

function subjectData(topicStats) {
  const map = {};
  for (const t of topicStats) {
    if (!t.subject) continue;
    if (!map[t.subject]) map[t.subject] = { subject: t.subject, totalCorrect: 0, totalQuestions: 0, attempts: 0 };
    map[t.subject].totalCorrect += t.totalCorrect || 0;
    map[t.subject].totalQuestions += t.totalQuestions || 0;
    map[t.subject].attempts += t.attempts || 0;
  }
  return Object.values(map).map((s) => ({
    ...s,
    accuracy: s.totalQuestions > 0 ? s.totalCorrect / s.totalQuestions : 0,
  }));
}

function statusLabel(acc) {
  if (acc >= 0.8) return { text: "Mastered", cls: "bg-emerald-100 text-emerald-700" };
  if (acc >= 0.6) return { text: "Needs Work", cls: "bg-amber-100 text-amber-700" };
  return { text: "Weak", cls: "bg-red-100 text-red-700" };
}

function attemptDate(a) {
  if (a?.takenAt?.toDate) return a.takenAt.toDate();
  if (a?.takenAt) { const d = new Date(a.takenAt); return isNaN(d) ? null : d; }
  return null;
}

// This week vs last week accuracy & XP (for KPI deltas).
function weeklyStats(attempts) {
  const cur = weekKey();
  const prev = weekKey(new Date(Date.now() - 7 * 86400000));
  const acc = {}, xp = {};
  for (const a of attempts) {
    const d = attemptDate(a);
    if (!d) continue;
    const wk = weekKey(d);
    if (!acc[wk]) acc[wk] = { correct: 0, total: 0 };
    acc[wk].correct += a.correct || 0;
    acc[wk].total += a.total || 0;
    xp[wk] = (xp[wk] || 0) + (a.xpEarned || 0);
  }
  const pct = (wk) => (acc[wk] && acc[wk].total ? Math.round((acc[wk].correct / acc[wk].total) * 100) : null);
  const c = pct(cur), p = pct(prev);
  return { curAcc: c, prevAcc: p, accDelta: c != null && p != null ? c - p : null, curXp: xp[cur] || 0 };
}

// Small ▲/▼ delta pill.
function Delta({ value, suffix = "" }) {
  if (value == null || value === 0) return null;
  const up = value > 0;
  return (
    <span className={`inline-flex items-center text-[11px] font-bold ${up ? "text-emerald-600" : "text-red-500"}`}>
      {up ? <HiArrowSmUp /> : <HiArrowSmDown />}{Math.abs(value)}{suffix}
    </span>
  );
}

export default function StudentProgress() {
  const { user, profile } = useAuth();
  const [topicStats, setTopicStats] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  // controls
  const [trendMetric, setTrendMetric] = useState("accuracy"); // "accuracy" | "xp"
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [topicSearch, setTopicSearch] = useState("");
  const [topicSort, setTopicSort] = useState("accuracy"); // "accuracy" | "attempts"

  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [tsSnap, atSnap] = await Promise.all([
          getDocs(collection(db, "students", user.uid, "topicStats")),
          getDocs(query(collection(db, "students", user.uid, "attempts"), orderBy("takenAt", "desc"), limit(100))),
        ]);
        if (!active) return;
        setTopicStats(tsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setAttempts(atSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("Progress page load failed", e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [user]);

  const subjects = useMemo(() => subjectData(topicStats), [topicStats]);
  const subjectList = useMemo(() => ["all", ...Array.from(new Set(topicStats.map((t) => t.subject).filter(Boolean)))], [topicStats]);

  const overallAccuracy = useMemo(() => {
    const total = topicStats.reduce((s, t) => s + (t.totalQuestions || 0), 0);
    const correct = topicStats.reduce((s, t) => s + (t.totalCorrect || 0), 0);
    return total > 0 ? Math.round((correct / total) * 100) : null;
  }, [topicStats]);

  // Chronological (oldest→newest) attempts for the trend.
  const chrono = useMemo(() => [...attempts].reverse().filter((a) => attemptDate(a)), [attempts]);
  const trendData = useMemo(() => {
    const last = chrono.slice(-20);
    return last.map((a) => {
      const d = attemptDate(a);
      const label = d ? d.toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "";
      return { label, value: trendMetric === "accuracy" ? (a.pct ?? 0) : (a.xpEarned ?? 0) };
    });
  }, [chrono, trendMetric]);

  const week = useMemo(() => weeklyStats(attempts), [attempts]);
  const gp = goalProgress(profile);

  const bestSubject = useMemo(() => {
    const withData = subjects.filter((s) => s.totalQuestions > 0);
    if (!withData.length) return null;
    return withData.reduce((b, s) => (s.accuracy > b.accuracy ? s : b));
  }, [subjects]);

  const personalBest = useMemo(() => attempts.reduce((m, a) => Math.max(m, a.pct ?? 0), 0), [attempts]);

  const passRate = useMemo(() => {
    if (!attempts.length) return null;
    const pass = attempts.filter((a) => (a.pct ?? 0) >= 50).length;
    return { pct: Math.round((pass / attempts.length) * 100), pass, total: attempts.length };
  }, [attempts]);

  const examVsPractice = useMemo(() => {
    const agg = { exam: { n: 0, correct: 0, total: 0 }, practice: { n: 0, correct: 0, total: 0 } };
    for (const a of attempts) {
      const k = a.mode === "exam" ? "exam" : "practice";
      agg[k].n += 1;
      agg[k].correct += a.correct || 0;
      agg[k].total += a.total || 0;
    }
    const avg = (g) => (g.total ? Math.round((g.correct / g.total) * 100) : null);
    return { exam: { ...agg.exam, avg: avg(agg.exam) }, practice: { ...agg.practice, avg: avg(agg.practice) } };
  }, [attempts]);

  const strongTopics = useMemo(
    () => topicStats.filter((t) => (t.attempts || 0) >= 1 && (t.accuracy ?? 0) >= 0.8).sort((a, b) => (b.accuracy ?? 0) - (a.accuracy ?? 0)),
    [topicStats]
  );
  const weakTopics = useMemo(
    () => topicStats.filter((t) => (t.attempts || 0) >= 1 && (t.accuracy ?? 1) < WEAK_THRESHOLD).sort((a, b) => (a.accuracy ?? 1) - (b.accuracy ?? 1)),
    [topicStats]
  );

  const masteryDist = useMemo(() => {
    let mastered = 0, needs = 0, weak = 0;
    for (const t of topicStats) {
      if ((t.attempts || 0) < 1) continue;
      const a = t.accuracy ?? 0;
      if (a >= 0.8) mastered++; else if (a >= 0.6) needs++; else weak++;
    }
    return { mastered, needs, weak };
  }, [topicStats]);

  const activeDays = useMemo(() => {
    const set = new Set();
    for (const a of attempts) {
      const d = attemptDate(a);
      if (d) set.add(d.toDateString());
    }
    return set.size;
  }, [attempts]);

  // Insight chips (cap 4, only with real data).
  const insights = useMemo(() => {
    const out = [];
    if (week.accDelta != null && week.accDelta !== 0) {
      const up = week.accDelta > 0;
      out.push({ icon: up ? "📈" : "📉", text: `${up ? "+" : ""}${week.accDelta}% accuracy vs last week`, tone: up ? "emerald" : "amber" });
    }
    if (passRate) out.push({ icon: "⚡", text: `${passRate.pct}% pass rate (${passRate.pass}/${passRate.total})`, tone: "indigo" });
    if (bestSubject) out.push({ icon: "🏆", text: `Strongest: ${bestSubject.subject} at ${Math.round(bestSubject.accuracy * 100)}%`, tone: "emerald" });
    if (weakTopics[0]) out.push({ icon: "🎯", text: `Focus on ${weakTopics[0].topic} (${Math.round((weakTopics[0].accuracy ?? 0) * 100)}%)`, tone: "red" });
    if ((profile?.streak?.current || 0) >= 3) out.push({ icon: "🔥", text: `${profile.streak.current}-day streak — keep it going!`, tone: "orange" });
    return out.slice(0, 4);
  }, [week, passRate, bestSubject, weakTopics, profile]);

  // Topic table: search + sort
  const tableTopics = useMemo(() => {
    const term = topicSearch.trim().toLowerCase();
    let list = topicStats.filter((t) =>
      !term || t.topic?.toLowerCase().includes(term) || t.subject?.toLowerCase().includes(term)
    );
    list = [...list].sort((a, b) =>
      topicSort === "attempts" ? (b.attempts || 0) - (a.attempts || 0) : (b.accuracy ?? 0) - (a.accuracy ?? 0)
    );
    return list;
  }, [topicStats, topicSearch, topicSort]);

  // Quiz history
  const filteredAttempts = useMemo(() => {
    if (subjectFilter === "all") return attempts;
    return attempts.filter((a) => a.subject === subjectFilter || a.topic?.toLowerCase().includes(subjectFilter.toLowerCase()));
  }, [attempts, subjectFilter]);
  const totalPages = Math.ceil(filteredAttempts.length / PAGE_SIZE);
  const pageAttempts = filteredAttempts.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  if (!profile) return <Loader />;

  const TONE = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-100",
    red: "bg-red-50 text-red-700 border-red-100",
    orange: "bg-orange-50 text-orange-700 border-orange-100",
  };

  const materialsLink = (t) =>
    t.subject && t.board && t.grade ? `/resources/${t.board}/${t.grade}/${t.subject}` : "/practice";

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title="My Progress" description="Your quiz analytics, trends, streak calendar and topic mastery." canonical="/progress" />

      {/* Light in-shell page header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-indigo-500 text-xs font-semibold uppercase tracking-widest">
              {profile.board ? BOARD_LABELS[profile.board] || profile.board : "Student"}
              {profile.grade ? ` · Class ${profile.grade}` : ""}
            </p>
            <span className="text-[10px] font-bold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">Level {profile.level || 1}</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-gray-800">📈 Your Progress</h1>
        </div>
      </div>

      {loading ? (
        <div className="py-20"><Loader /></div>
      ) : (
        <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-8 space-y-6">

          {/* ── KPI CARDS ── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {/* Accuracy */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <p className="text-2xl font-black text-gray-800">
                {overallAccuracy !== null ? <><AnimatedCounter value={overallAccuracy} />%</> : <span className="text-gray-300 text-base">—</span>}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <p className="text-[11px] font-semibold text-gray-400">Accuracy</p>
                <Delta value={week.accDelta} suffix="%" />
              </div>
            </div>
            {/* Quizzes */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <p className="text-2xl font-black text-gray-800"><AnimatedCounter value={profile.totalAttempts || 0} /></p>
              <p className="text-[11px] font-semibold text-gray-400 mt-0.5">Quizzes taken</p>
            </div>
            {/* Streak */}
            <div className="bg-orange-50 rounded-2xl border border-orange-100 shadow-sm p-4">
              <p className="text-2xl font-black text-orange-600 flex items-center gap-1">
                <HiFire /> <AnimatedCounter value={profile.streak?.current || 0} />
              </p>
              <p className="text-[11px] font-semibold text-orange-400 mt-0.5">Day streak · best {profile.streak?.longest || 0}</p>
            </div>
            {/* This-week XP vs goal */}
            <div className="bg-indigo-50 rounded-2xl border border-indigo-100 shadow-sm p-4">
              <p className="text-2xl font-black text-indigo-600"><AnimatedCounter value={currentWeeklyXp(profile)} /></p>
              <p className="text-[11px] font-semibold text-indigo-400 mt-0.5">XP this week / {gp.goal}</p>
            </div>
            {/* Best subject */}
            <div className="bg-emerald-50 rounded-2xl border border-emerald-100 shadow-sm p-4">
              <p className="text-lg font-black text-emerald-700 capitalize truncate">{bestSubject ? bestSubject.subject : "—"}</p>
              <p className="text-[11px] font-semibold text-emerald-500 mt-0.5">
                {bestSubject ? `Best subject · ${Math.round(bestSubject.accuracy * 100)}%` : "Best subject"}
              </p>
            </div>
            {/* Personal best */}
            <div className="bg-amber-50 rounded-2xl border border-amber-100 shadow-sm p-4">
              <p className="text-2xl font-black text-amber-600 flex items-center gap-1">
                <HiStar /> {personalBest ? <><AnimatedCounter value={personalBest} />%</> : "—"}
              </p>
              <p className="text-[11px] font-semibold text-amber-500 mt-0.5">Personal best</p>
            </div>
          </div>

          {/* ── PERFORMANCE TREND ── */}
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <HiTrendingUp className="text-indigo-500 text-xl" />
                <h2 className="font-extrabold text-gray-800 text-lg">Performance Trend</h2>
              </div>
              <div className="inline-flex bg-slate-100 rounded-xl p-1">
                <button onClick={() => setTrendMetric("accuracy")}
                  className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors ${trendMetric === "accuracy" ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500"}`}>
                  Accuracy %
                </button>
                <button onClick={() => setTrendMetric("xp")}
                  className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors ${trendMetric === "xp" ? "bg-white text-indigo-600 shadow-sm" : "text-gray-500"}`}>
                  XP earned
                </button>
              </div>
            </div>
            <TrendChart
              data={trendData}
              unit={trendMetric === "accuracy" ? "%" : "XP"}
              color={trendMetric === "accuracy" ? "#6366f1" : "#f59e0b"}
            />
            {trendData.length > 0 && (
              <p className="text-xs text-gray-400 text-center mt-2">Your last {trendData.length} quiz{trendData.length !== 1 ? "zes" : ""}, oldest → newest.</p>
            )}
          </motion.div>

          {/* ── INSIGHTS + EXAM vs PRACTICE ── */}
          {(insights.length > 0 || examVsPractice.exam.n + examVsPractice.practice.n > 0) && (
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-6"
            >
              {/* Insights */}
              <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="font-extrabold text-gray-800 text-lg mb-4">💡 Smart Insights</h2>
                {insights.length === 0 ? (
                  <p className="text-sm text-gray-400">Take a few quizzes to unlock personalised insights.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {insights.map((ins, i) => (
                      <span key={i} className={`inline-flex items-center gap-1.5 border text-sm font-semibold px-3 py-2 rounded-xl ${TONE[ins.tone]}`}>
                        <span className="text-base">{ins.icon}</span> {ins.text}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Exam vs Practice */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="font-extrabold text-gray-800 text-lg mb-4">Exam vs Practice</h2>
                {[
                  { key: "exam", label: "Exam mode", icon: <HiClock />, cls: "text-amber-600", g: examVsPractice.exam },
                  { key: "practice", label: "Practice", icon: <HiPlay />, cls: "text-emerald-600", g: examVsPractice.practice },
                ].map((row) => (
                  <div key={row.key} className="flex items-center gap-3 py-2">
                    <span className={`text-lg ${row.cls}`}>{row.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-800">{row.label}</p>
                      <p className="text-xs text-gray-400">{row.g.n} taken</p>
                    </div>
                    <span className="text-lg font-black text-gray-800">{row.g.avg != null ? `${row.g.avg}%` : "—"}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* ── STRENGTHS & FOCUS ── */}
          {(strongTopics.length > 0 || weakTopics.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Strengths */}
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="font-extrabold text-gray-800 text-lg mb-4">💪 Your Strengths</h2>
                {strongTopics.length === 0 ? (
                  <p className="text-sm text-gray-400 py-2">Keep practising to build mastery (80%+ on a topic).</p>
                ) : (
                  <div className="space-y-2">
                    {strongTopics.slice(0, 5).map((t) => (
                      <div key={t.id} className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50/50 border border-emerald-50">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-gray-800 truncate">{t.topic}</p>
                          {t.subject && <p className="text-[11px] text-gray-400 capitalize">{t.subject}</p>}
                        </div>
                        <span className="text-sm font-black text-emerald-600">{Math.round((t.accuracy ?? 0) * 100)}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>

              {/* Focus areas */}
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="font-extrabold text-gray-800 text-lg mb-4">🎯 Focus Areas</h2>
                {weakTopics.length === 0 ? (
                  <p className="text-sm text-gray-400 py-2">No weak topics right now — great work! 🌟</p>
                ) : (
                  <div className="space-y-2">
                    {weakTopics.slice(0, 5).map((t) => (
                      <div key={t.id} className="flex items-center gap-3 p-3 rounded-xl bg-red-50/50 border border-red-50">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-gray-800 truncate">{t.topic}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex-1 h-1.5 bg-red-100 rounded-full overflow-hidden max-w-[100px]">
                              <div className="h-full bg-red-400 rounded-full" style={{ width: `${Math.round((t.accuracy ?? 0) * 100)}%` }} />
                            </div>
                            <span className="text-xs font-bold text-red-500">{Math.round((t.accuracy ?? 0) * 100)}%</span>
                          </div>
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          <Link to="/mcq" className="text-xs font-bold bg-indigo-600 text-white px-2.5 py-1 rounded-lg hover:bg-indigo-700 transition-colors">Retake</Link>
                          <Link to={materialsLink(t)} className="text-xs font-bold bg-white border border-gray-200 text-gray-600 px-2.5 py-1 rounded-lg hover:bg-gray-50 transition-colors">Study</Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            </div>
          )}

          {/* ── SUBJECT MASTERY ── */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <HiTrendingUp className="text-indigo-500 text-xl" />
                <h2 className="font-extrabold text-gray-800 text-lg">Subject Accuracy</h2>
              </div>
              {(masteryDist.mastered + masteryDist.needs + masteryDist.weak) > 0 && (
                <div className="flex items-center gap-3 text-xs font-semibold">
                  <span className="text-emerald-600">● {masteryDist.mastered} mastered</span>
                  <span className="text-amber-600">● {masteryDist.needs} needs work</span>
                  <span className="text-red-500">● {masteryDist.weak} weak</span>
                </div>
              )}
            </div>
            <SubjectBars data={subjects} compact={false} />
          </motion.div>

          {/* ── ACTIVITY HEATMAP ── */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-1">
              <h2 className="font-extrabold text-gray-800 text-lg">Activity — Last 90 Days</h2>
              <p className="text-xs text-gray-400">{activeDays} active day{activeDays !== 1 ? "s" : ""} · {attempts.length} recent quizzes</p>
            </div>
            <StreakCalendar attempts={attempts} />
          </motion.div>

          {/* ── TOPIC MASTERY TABLE ── */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="font-extrabold text-gray-800 text-lg">Topic Mastery</h2>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <HiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    value={topicSearch}
                    onChange={(e) => setTopicSearch(e.target.value)}
                    placeholder="Search topics…"
                    className="pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 w-36 sm:w-44"
                  />
                </div>
                <select
                  value={topicSort}
                  onChange={(e) => setTopicSort(e.target.value)}
                  className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                >
                  <option value="accuracy">By accuracy</option>
                  <option value="attempts">By attempts</option>
                </select>
              </div>
            </div>
            {tableTopics.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">
                {topicStats.length === 0 ? "No topic data yet. Take some quizzes!" : "No topics match your search."}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100">
                      <th className="pb-3 pr-4">Topic</th>
                      <th className="pb-3 pr-4">Subject</th>
                      <th className="pb-3 pr-4 text-center">Attempts</th>
                      <th className="pb-3 pr-4 min-w-[120px]">Accuracy</th>
                      <th className="pb-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {tableTopics.map((t) => {
                      const acc = t.accuracy ?? 0;
                      const pct = Math.round(acc * 100);
                      const status = statusLabel(acc);
                      return (
                        <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                          <td className="py-3 pr-4 font-semibold text-gray-800 max-w-[200px] truncate">{t.topic || "—"}</td>
                          <td className="py-3 pr-4 text-gray-500 capitalize">{t.subject || "—"}</td>
                          <td className="py-3 pr-4 text-center text-gray-600">{t.attempts}</td>
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${acc >= 0.8 ? "bg-emerald-400" : acc >= 0.6 ? "bg-amber-400" : "bg-red-400"}`} style={{ width: `${pct}%` }} />
                              </div>
                              <span className="text-xs font-bold text-gray-600 w-8 text-right">{pct}%</span>
                            </div>
                          </td>
                          <td className="py-3">
                            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${status.cls}`}>{status.text}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>

          {/* ── QUIZ HISTORY ── */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="font-extrabold text-gray-800 text-lg">Quiz History</h2>
              <div className="flex items-center gap-2">
                <HiFilter className="text-gray-400" />
                <select
                  value={subjectFilter}
                  onChange={(e) => { setSubjectFilter(e.target.value); setPage(0); }}
                  className="text-sm border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                >
                  {subjectList.map((s) => (
                    <option key={s} value={s}>{s === "all" ? "All Subjects" : s}</option>
                  ))}
                </select>
              </div>
            </div>

            {filteredAttempts.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No quiz history yet.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100">
                        <th className="pb-3 pr-4">Date</th>
                        <th className="pb-3 pr-4">Topic</th>
                        <th className="pb-3 pr-4">Mode</th>
                        <th className="pb-3 pr-4 text-center">Score</th>
                        <th className="pb-3 text-center">XP</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {pageAttempts.map((a) => {
                        const pass = (a.pct ?? 0) >= 50;
                        const exam = a.mode === "exam";
                        const when = attemptDate(a)
                          ? attemptDate(a).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
                          : "—";
                        return (
                          <tr key={a.id} className="hover:bg-gray-50 transition-colors">
                            <td className="py-3 pr-4 text-gray-500 whitespace-nowrap">{when}</td>
                            <td className="py-3 pr-4 font-semibold text-gray-800 max-w-[160px] truncate">
                              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle ${pass ? "bg-green-400" : "bg-red-400"}`} />
                              {a.topic || "Quiz"}
                            </td>
                            <td className="py-3 pr-4">
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${exam ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                                {exam ? "Exam" : "Practice"}
                              </span>
                            </td>
                            <td className="py-3 pr-4 text-center">
                              <span className={`text-xs font-bold px-2.5 py-1 rounded-lg ${pass ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                                {a.correct}/{a.total} · {a.pct}%
                              </span>
                            </td>
                            <td className="py-3 text-center">
                              <span className="text-xs font-bold text-indigo-600">+{a.xpEarned || 0}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                    <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                      className="flex items-center gap-1 text-sm font-semibold text-gray-600 disabled:opacity-40 hover:text-indigo-600 transition-colors">
                      <HiChevronLeft /> Previous
                    </button>
                    <span className="text-xs text-gray-400">Page {page + 1} of {totalPages}</span>
                    <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                      className="flex items-center gap-1 text-sm font-semibold text-gray-600 disabled:opacity-40 hover:text-indigo-600 transition-colors">
                      Next <HiChevronRight />
                    </button>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}
